import type { QuickBooksFpaStatementRender } from '../../../data/ServerApiClient'
import { formatAsOf } from './cfoFormat'

export type CfoStatementComparisonMode =
  | 'prior_month'
  | 'prior_quarter'
  | 'prior_year'
  | 'ytd'
  | 'rolling_12'
  | 'rolling_quarters'
  | 'rolling_years'
  | 'rolling_3_vs_prior_3'
  | 'rolling_12_vs_prior_12'

export type CfoStatementColumnSpec = {
  key: string
  label: string
  kind: 'value' | 'delta' | 'pct'
  period?: string | null
  periodIndex?: number
  indices?: number[]
  currentIndices?: number[]
  priorIndices?: number[]
}

/** Original observed history; comparison-buffer months are not display history. */
export type CfoStatementColumnWindow = {
  months: number
  selectedPeriodEnd?: string | null
}

const periodKey = (period: string | null | undefined) => {
  const match = /^(\d{4})-(\d{2})/.exec(String(period || ''))
  return match ? `${match[1]}-${match[2]}` : ''
}

const monthOffset = (period: string | null | undefined, offset: number) => {
  const key = periodKey(period)
  if (!key) return ''
  const [yearRaw, monthRaw] = key.split('-').map(Number)
  const date = new Date(Date.UTC(yearRaw, monthRaw - 1 + offset, 1))
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
}

const formatMonthHeader = (period: string | null | undefined) =>
  formatAsOf(period).replace(/\s+\d{4}$/, '').slice(0, 3).toUpperCase()

const formatQuarterHeader = (period: string | null | undefined) => {
  const key = periodKey(period)
  if (!key) return 'Q'
  const [yearRaw, monthRaw] = key.split('-').map(Number)
  return `Q${Math.floor((monthRaw - 1) / 3) + 1} ${yearRaw}`
}

const yearCoverageLabel = (periods: string[], indices: number[], selectedPeriodEnd?: string | null) => {
  const keys = indices.map((index) => periodKey(periods[index]))
  const year = keys[0]?.slice(0, 4) || 'Year'
  const months = keys.map((key) => Number(key.slice(5)))
  const contiguous = months.every((month, index) => index === 0 || month === months[index - 1] + 1)
  const cutoff = /^(\d{4})-(\d{2})-(\d{2})$/.exec(selectedPeriodEnd || '')
  const cutoffDay = cutoff ? Number(cutoff[3]) : 0
  const cutoffMonth = cutoff ? Number(cutoff[2]) : 0
  const cutoffYear = cutoff ? Number(cutoff[1]) : 0
  const leap = cutoffYear % 4 === 0 && (cutoffYear % 100 !== 0 || cutoffYear % 400 === 0)
  const cutoffLastDay = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][cutoffMonth - 1] || 0
  const validCutoff = cutoff && cutoffYear >= 1 && cutoffMonth >= 1 && cutoffMonth <= 12 && cutoffDay >= 1 && cutoffDay <= cutoffLastDay
  const finalMonthPartial = validCutoff && `${cutoff[1]}-${cutoff[2]}` === keys[keys.length - 1] && cutoffDay < cutoffLastDay
  if (contiguous && months.length === 12 && months[0] === 1 && months[11] === 12 && !finalMonthPartial) return year
  const names = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
  const first = names[months[0] - 1] || '?'
  const last = names[months[months.length - 1] - 1] || '?'
  const range = first === last ? first : `${first}–${last}`
  return contiguous && months[0] === 1
    ? `${year} YTD · ${range}`
    : `${year} · ${range} (partial)`
}

const periodBucketKey = (period: string | null | undefined, bucket: 'quarter' | 'year') => {
  const key = periodKey(period)
  if (!key) return ''
  const [yearRaw, monthRaw] = key.split('-').map(Number)
  if (bucket === 'year') return String(yearRaw)
  return `${yearRaw}-Q${Math.floor((monthRaw - 1) / 3) + 1}`
}

export const buildCfoStatementColumnSpecs = (
  statement: Pick<QuickBooksFpaStatementRender, 'periods'>,
  endPeriod: string | null | undefined,
  comparisonMode: CfoStatementComparisonMode,
  maxPeriods: number,
  periodWindow?: CfoStatementColumnWindow,
): CfoStatementColumnSpec[] => {
  const periods = statement.periods
  if (!periods.length) return []
  const indexByKey = new Map(periods.map((period, index) => [periodKey(period), index]))
  const fallbackIndex = Math.max(0, periods.length - 1)
  const selectedIndex = indexByKey.get(periodKey(endPeriod)) ?? fallbackIndex
  const selectedPeriod = periods[selectedIndex]
  const value = (key: string, label: string, indices: number[], periodIndex?: number): CfoStatementColumnSpec => ({
    key,
    label,
    kind: 'value',
    period: indices.length === 1 ? periods[indices[0]] || null : null,
    periodIndex,
    indices,
  })
  const comparison = (key: string, label: string, currentIndices: number[], priorIndices: number[], kind: 'delta' | 'pct'): CfoStatementColumnSpec => ({
    key,
    label,
    kind,
    period: currentIndices.length === 1 ? periods[currentIndices[0]] || null : null,
    currentIndices,
    priorIndices,
  })
  const comparativeColumns = (key: string, priorLabel: string, currentLabel: string, priorIndices: number[], currentIndices: number[]) => [
    value(`${key}:prior`, priorLabel, priorIndices, priorIndices.length === 1 ? priorIndices[0] : undefined),
    value(`${key}:current`, currentLabel, currentIndices, currentIndices.length === 1 ? currentIndices[0] : undefined),
    comparison(`${key}:delta`, 'Change', currentIndices, priorIndices, 'delta'),
    comparison(`${key}:pct`, 'Change %', currentIndices, priorIndices, 'pct'),
  ]

  if (comparisonMode === 'rolling_12') {
    const start = Math.max(0, selectedIndex - Math.max(1, maxPeriods) + 1)
    const windowStart = periodWindow ? monthOffset(selectedPeriod, -Math.max(1, periodWindow.months) + 1) : null
    return periods.flatMap((period, index) => {
      if (index > selectedIndex || (windowStart ? periodKey(period) < windowStart : index < start)) return []
      return [value(`period:${periodKey(period)}:${index}`, formatMonthHeader(period), [index], index)]
    })
  }

  if (comparisonMode === 'rolling_quarters' || comparisonMode === 'rolling_years') {
    const bucketKind = comparisonMode === 'rolling_quarters' ? 'quarter' : 'year'
    const buckets: Array<{ key: string; label: string; indices: number[] }> = []
    const windowStart = periodWindow ? monthOffset(selectedPeriod, -Math.max(1, periodWindow.months) + 1) : null
    periods.forEach((period, index) => {
      if (index > selectedIndex || (windowStart && periodKey(period) < windowStart)) return
      const key = periodBucketKey(period, bucketKind)
      if (!key) return
      const previous = buckets[buckets.length - 1]
      if (previous?.key === key) previous.indices.push(index)
      else buckets.push({
        key,
        label: bucketKind === 'quarter' ? formatQuarterHeader(period) : '',
        indices: [index],
      })
    })
    const observedBuckets = periodWindow ? buckets : buckets.slice(-Math.max(1, maxPeriods))
    return observedBuckets.map((bucket) => value(`${bucketKind}:${bucket.key}`,
      bucketKind === 'year' ? yearCoverageLabel(periods, bucket.indices, periodWindow?.selectedPeriodEnd) : bucket.label,
      bucket.indices))
  }

  if (comparisonMode === 'rolling_12_vs_prior_12') {
    const currentStart = Math.max(0, selectedIndex - 11)
    const currentIndices = Array.from({ length: selectedIndex - currentStart + 1 }, (_, index) => currentStart + index)
    const priorStart = Math.max(0, currentStart - 12)
    const priorIndices = Array.from({ length: currentStart - priorStart }, (_, index) => priorStart + index)
    return comparativeColumns('rolling12', 'Prior 12', 'Last 12', priorIndices, currentIndices)
  }

  if (comparisonMode === 'rolling_3_vs_prior_3') {
    const currentStart = Math.max(0, selectedIndex - 2)
    const currentIndices = Array.from({ length: selectedIndex - currentStart + 1 }, (_, index) => currentStart + index)
    const priorStart = Math.max(0, currentStart - 3)
    const priorIndices = Array.from({ length: currentStart - priorStart }, (_, index) => priorStart + index)
    return comparativeColumns('rolling3', 'Prior 3', 'Last 3', priorIndices, currentIndices)
  }

  if (comparisonMode === 'ytd') {
    const selectedKey = periodKey(selectedPeriod)
    const [yearRaw, monthRaw] = selectedKey.split('-').map(Number)
    const currentIndices = periods
      .map((period, index) => ({ key: periodKey(period), index }))
      .filter(({ key }) => key >= `${yearRaw}-01` && key <= `${yearRaw}-${String(monthRaw).padStart(2, '0')}`)
      .map(({ index }) => index)
    const priorYear = yearRaw - 1
    const priorIndices = periods
      .map((period, index) => ({ key: periodKey(period), index }))
      .filter(({ key }) => key >= `${priorYear}-01` && key <= `${priorYear}-${String(monthRaw).padStart(2, '0')}`)
      .map(({ index }) => index)
    return comparativeColumns('ytd', `YTD ${priorYear}`, `YTD ${yearRaw}`, priorIndices, currentIndices)
  }

  const priorIndex = comparisonMode === 'prior_year'
    ? indexByKey.get(monthOffset(selectedPeriod, -12)) ?? -1
    : comparisonMode === 'prior_quarter'
      ? selectedIndex - 3
      : selectedIndex - 1
  const priorLabel = comparisonMode === 'prior_year'
    ? formatAsOf(periods[priorIndex] || monthOffset(selectedPeriod, -12))
    : comparisonMode === 'prior_quarter'
      ? periods[priorIndex] ? formatAsOf(periods[priorIndex]).replace(/\s+\d{4}$/, '') : 'Prior quarter'
      : periods[priorIndex] ? formatAsOf(periods[priorIndex]).replace(/\s+\d{4}$/, '') : 'Prior'
  const currentLabel = comparisonMode === 'prior_year'
    ? formatAsOf(selectedPeriod)
    : formatAsOf(selectedPeriod).replace(/\s+\d{4}$/, '')
  return comparativeColumns('period', priorLabel, currentLabel, priorIndex >= 0 ? [priorIndex] : [], [selectedIndex])
}
